/**
 * Application Entry Point
 * Orchestrates initialization and coordinates between modules
 */

import { loadConfig } from '@/config';
import { Logger } from '@/utils/logger';
import {
    GeolocationService,
    BusStopService,
    TrainStationService,
    TrainDepartureService,
    setUserLocation,
    initializeState,
    isWithinChelmsfordArea,
    OUTSIDE_AREA_MESSAGE,
    type DisplayItem,
} from '@/core';
import { reverseGeocodeToPostcode, geocodePostcode } from '@/api';
import { debounce } from '@/utils/helpers';
import { FavoritesManager } from '@/utils/favorites';
import { saveLocation, getSavedLocation } from '@/utils/location-storage';
import type { Coordinates, DepartureBoard } from '@/types';
import {
    displayItems,
    displayError,
    showPostcodeEntryForm,
    updatePostcodeDisplay,
    hidePostcodeForm,
    showRefreshContainer,
    showPostcodeError,
    clearPostcodeError,
    setPostcodeFormBusy,
    handleRefresh,
    setupAllHandlers,
    setSetupHandlersCallback,
    setupPostcodeDisplayClickHandler,
    setupLocationUpdateHandler,
    showLoadingDepartures,
} from '@/ui';
import {
    initializeSettings,
    getTextSize,
    setTextSize,
    applyTextSize,
    getHighContrast,
    setHighContrast,
    applyHighContrast,
    isDarkMode,
    setColorScheme,
    applyColorScheme,
    type TextSize,
} from '@/utils/settings';
import { setupHelpHandlers, showHelpIfFirstVisit } from '@/ui/help';
import { setupDisplaySettingsHandlers } from '@/ui/display-settings';
import { initializeRouteFilter } from '@/ui/route-filter';

// PWA install prompt
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const INSTALL_DISMISS_KEY = 'install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let autoRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
let lastSuccessfulPostcode: string | null = null;

/**
 * Check if install banner was recently dismissed
 */
function isInstallDismissed(): boolean {
    const dismissed = localStorage.getItem(INSTALL_DISMISS_KEY);
    if (!dismissed) return false;
    const dismissedAt = parseInt(dismissed, 10);
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

/**
 * Show install banner if not dismissed
 */
function showInstallBanner(): void {
    const banner = document.getElementById('install-banner');
    if (banner && !isInstallDismissed()) {
        banner.hidden = false;
    }
}

/**
 * Hide install banner
 */
function hideInstallBanner(): void {
    const banner = document.getElementById('install-banner');
    if (banner) {
        banner.hidden = true;
    }
}

/**
 * Fetch and display departures for both directions, combined with train stations
 */
async function fetchAndDisplayDepartures(location: Coordinates): Promise<void> {
    // Get train stations first so we can reference them for error cases
    const trainStations = TrainStationService.getStationsByDistance(location);

    // Get favorite ATCO codes to fetch them regardless of distance
    const favoriteAtcoCodes = Array.from(FavoritesManager.getAtcoCodes());

    // Fetch favorite stops, nearby bus stops, and train data in parallel
    const [favoriteStops, busResult, trainResults] = await Promise.all([
        BusStopService.getByAtcoCodes(favoriteAtcoCodes, location),
        BusStopService.getBothDirections(location),
        TrainDepartureService.getDeparturesForAllStations(trainStations),
    ]);

    // Convert train results to display items (include both successful and failed)
    const trainItems: DisplayItem[] = trainResults.map((r, index) => {
        if (r.success) {
            return {
                type: 'train' as const,
                data: r.board,
            };
        }
        // Create a placeholder board for failed stations
        return {
            type: 'train' as const,
            data: {
                station: trainStations[index],
                departures: [],
                lastUpdated: Date.now(),
                isStale: false,
            },
            errorMessage: r.error.getUserMessage(),
        };
    });

    // Fetch departures for favorite stops that aren't already in nearby results
    const nearbyAtcoCodes = new Set(
        busResult.success ? busResult.boards.map(b => b.stop.atcoCode) : []
    );
    const favoritesNotNearby = favoriteStops.filter(stop => !nearbyAtcoCodes.has(stop.atcoCode));

    // Fetch departures for distant favorites
    const favoriteBoards: DepartureBoard[] = [];
    if (favoritesNotNearby.length > 0) {
        const favResults = await Promise.allSettled(
            favoritesNotNearby.map(stop => BusStopService.getDeparturesForStop(stop))
        );
        for (const result of favResults) {
            if (result.status === 'fulfilled') {
                favoriteBoards.push(result.value);
            }
        }
    }

    if (!busResult.success) {
        // Still show favorites and train departures even if nearby bus data fails
        const favItems: DisplayItem[] = favoriteBoards.map(b => ({ type: 'bus', data: b }));
        if (favItems.length > 0 || trainItems.length > 0) {
            displayItems([...favItems, ...trainItems], false, setupAllHandlers);
        } else {
            displayError(busResult.error.getUserMessage());
        }
        return;
    }

    // Combine favorite bus items (distant ones) with nearby bus items
    const favBusItems: DisplayItem[] = favoriteBoards.map(b => ({ type: 'bus', data: b }));
    const nearbyBusItems: DisplayItem[] = busResult.boards.map(b => ({ type: 'bus', data: b }));

    // Show "show more" button since there are likely more stops nearby
    displayItems([...favBusItems, ...nearbyBusItems, ...trainItems], true, setupAllHandlers);
}

/**
 * Set up postcode form submission handler
 */
function setupPostcodeForm(): void {
    const postcodeForm = document.getElementById('postcode-form');
    const postcodeInput = document.getElementById('postcode-input');

    // Type guard for form elements
    if (
        !(postcodeForm instanceof HTMLFormElement) ||
        !(postcodeInput instanceof HTMLInputElement)
    ) {
        return;
    }

    postcodeForm.addEventListener('submit', e => {
        e.preventDefault();
        const enteredPostcode = postcodeInput.value.trim();
        if (!enteredPostcode) return;

        clearPostcodeError();
        setPostcodeFormBusy(true);
        updatePostcodeDisplay(
            '<span class="spinner" aria-hidden="true"></span>Finding your area...',
            true
        );
        hidePostcodeForm();

        void (async () => {
            try {
                const { coordinates, normalizedPostcode } = await geocodePostcode(enteredPostcode);
                if (!isWithinChelmsfordArea(coordinates)) {
                    showPostcodeError(
                        `${normalizedPostcode} is outside Chelmsford. This app only covers the Chelmsford area.`
                    );
                    showPostcodeEntryForm(
                        'Enter a Chelmsford postcode:',
                        lastSuccessfulPostcode ?? undefined
                    );
                    return;
                }
                setUserLocation(coordinates);
                lastSuccessfulPostcode = normalizedPostcode;
                updatePostcodeDisplay(`<span class="status">${normalizedPostcode}</span>`, true);
                await fetchAndDisplayDepartures(coordinates);
                showRefreshContainer();
            } catch (error) {
                Logger.error('Manual postcode lookup failed', String(error));
                showPostcodeError('Postcode not found. Please check and try again.');
                showPostcodeEntryForm('Enter your postcode:', lastSuccessfulPostcode ?? undefined);
            } finally {
                setPostcodeFormBusy(false);
            }
        })();
    });
}

/**
 * Set up install banner buttons
 */
function setupInstallBanner(): void {
    const installBtn = document.getElementById('install-btn');
    const dismissBtn = document.getElementById('install-dismiss');

    if (installBtn) {
        installBtn.addEventListener('click', () => {
            if (!deferredPrompt) {
                // Install prompt not available - hide banner and let user use browser's native install
                Logger.info('Install prompt not available, hiding banner');
                hideInstallBanner();
                return;
            }
            void (async () => {
                await deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                Logger.info(`Install prompt outcome: ${outcome}`);
                deferredPrompt = null;
                hideInstallBanner();
            })();
        });
    }

    if (dismissBtn) {
        dismissBtn.addEventListener('click', () => {
            localStorage.setItem(INSTALL_DISMISS_KEY, Date.now().toString());
            hideInstallBanner();
            Logger.info('Install banner dismissed');
        });
    }
}

/**
 * Set up refresh button
 */
function setupRefreshButton(): void {
    const refreshBtn = document.getElementById('refresh-btn');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', debounce(handleRefresh, 1000));
    }
}

/**
 * Set up auto-refresh interval
 */
function setupAutoRefresh(): void {
    autoRefreshIntervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
            void handleRefresh();
        }
    }, 60000);
}

/**
 * Set up text size toggle buttons
 */
function setupTextSizeButtons(): void {
    const buttons = document.querySelectorAll('.text-size-btn');
    const currentSize = getTextSize();

    // Set initial active state
    buttons.forEach(btn => {
        const size = btn.getAttribute('data-size') as TextSize;
        btn.classList.toggle('active', size === currentSize);
        btn.setAttribute('aria-pressed', size === currentSize ? 'true' : 'false');
    });

    // Add click handlers
    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            const size = btn.getAttribute('data-size') as TextSize;
            setTextSize(size);
            applyTextSize(size);

            // Update all button states
            buttons.forEach(b => {
                const bSize = b.getAttribute('data-size');
                b.classList.toggle('active', bSize === size);
                b.setAttribute('aria-pressed', bSize === size ? 'true' : 'false');
            });
        });
    });
}

/**
 * Set up high contrast toggle button
 */
function setupContrastButton(): void {
    const btn = document.getElementById('contrast-btn');
    if (!btn) return;

    const isActive = getHighContrast();
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    btn.addEventListener('click', () => {
        const newState = !getHighContrast();
        setHighContrast(newState);
        applyHighContrast(newState);
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-pressed', newState ? 'true' : 'false');
    });
}

/**
 * Set up dark mode toggle button
 */
function setupThemeButton(): void {
    const btn = document.getElementById('theme-btn');
    if (!btn) return;

    const updateButtonState = () => {
        const dark = isDarkMode();
        btn.classList.toggle('active', dark);
        btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
    };

    updateButtonState();

    btn.addEventListener('click', () => {
        const newScheme = isDarkMode() ? 'light' : 'dark';
        setColorScheme(newScheme);
        applyColorScheme(newScheme === 'dark');
        updateButtonState();
    });
}

function showPostcodeFallbackIfLocationIsSlow(): () => void {
    let locationStillPending = true;
    const timeoutId = setTimeout(() => {
        if (!locationStillPending) return;
        showPostcodeEntryForm(
            'Location is taking a while. Enter postcode instead:',
            lastSuccessfulPostcode ?? undefined,
            false
        );
    }, 4000);

    return () => {
        locationStillPending = false;
        clearTimeout(timeoutId);
    };
}

/**
 * Use a previously saved location and show its departures
 * @param note - Optional explanation shown next to the postcode
 */
async function showSavedLocation(
    savedLocation: NonNullable<ReturnType<typeof getSavedLocation>>,
    note?: string
): Promise<void> {
    setUserLocation(savedLocation.coordinates);
    const displayPostcode = savedLocation.postcode || 'Saved location';
    const noteHtml = note ? ` ${note}` : '';
    updatePostcodeDisplay(`<span class="status">${displayPostcode}</span>${noteHtml}`, true);
    showLoadingDepartures();
    await fetchAndDisplayDepartures(savedLocation.coordinates);
    showRefreshContainer();
}

/**
 * Initialize the application
 */
async function init(): Promise<void> {
    try {
        // Initialize accessibility settings first (applies saved text size, contrast)
        try {
            initializeSettings();
            setupTextSizeButtons();
            setupContrastButton();
            setupThemeButton();
            setupHelpHandlers();
            setupDisplaySettingsHandlers();
            initializeRouteFilter();
        } catch (settingsError) {
            Logger.warn('Settings initialization failed, continuing', settingsError);
        }

        const config = await loadConfig();
        Logger.setDebugMode(config.debug);
        Logger.success('Configuration loaded');

        // Initialize state from config
        initializeState(config.busStops.maxSearchRadius);

        // Set up the callback for re-rendering
        setSetupHandlersCallback(setupAllHandlers);

        const postcodeDisplay = document.getElementById('postcode-display');
        if (!postcodeDisplay) return;

        // Set up click handler on postcode display (so users can change location)
        setupPostcodeDisplayClickHandler();

        // Set up click handler on location update button (re-detect GPS)
        setupLocationUpdateHandler();

        // Set up manual postcode entry form (early, so it works if geolocation fails)
        setupPostcodeForm();

        // Set up refresh button, auto-refresh and install banner early too, so they work
        // whichever way the location ends up being set (GPS, saved location or postcode)
        setupRefreshButton();
        setupAutoRefresh();
        setupInstallBanner();

        // Check if geolocation is supported
        if (!GeolocationService.isSupported()) {
            showPostcodeEntryForm(
                'Geolocation not supported. Enter postcode:',
                lastSuccessfulPostcode ?? undefined
            );
            return;
        }

        // Initialize bus stop cache (non-blocking)
        BusStopService.init().catch(err => Logger.warn('Bus stop init failed', err));

        // Get browser location
        const clearSlowLocationFallback = showPostcodeFallbackIfLocationIsSlow();
        const result = await GeolocationService.getLocationFromBrowser();
        clearSlowLocationFallback();

        if (!result.success) {
            // Try to use saved location as fallback
            const savedLocation = getSavedLocation();
            if (savedLocation) {
                Logger.info('Using saved location as fallback');
                await showSavedLocation(savedLocation);
                return;
            }

            showPostcodeEntryForm(
                'Could not detect location. Enter postcode:',
                lastSuccessfulPostcode ?? undefined
            );
            return;
        }

        // Check if user appears far from Chelmsford (likely VPN user)
        if (!isWithinChelmsfordArea(result.location.coordinates)) {
            Logger.info('User far from Chelmsford, checking saved location');

            // Try to use saved location if it's within Chelmsford area
            const savedLocation = getSavedLocation();
            if (savedLocation && isWithinChelmsfordArea(savedLocation.coordinates)) {
                Logger.info('Using saved location (within Chelmsford)');
                await showSavedLocation(
                    savedLocation,
                    "You're outside Chelmsford, so showing your last Chelmsford location"
                );
                return;
            }

            displayError(OUTSIDE_AREA_MESSAGE);
            showPostcodeEntryForm(
                'Enter a Chelmsford postcode to find nearby stops:',
                lastSuccessfulPostcode ?? undefined
            );
            return;
        }

        // Store location in state
        setUserLocation(result.location.coordinates);
        hidePostcodeForm();

        // Reverse geocode to get postcode
        updatePostcodeDisplay(
            '<span class="spinner" aria-hidden="true"></span>Finding your area...',
            true
        );

        try {
            const postcode = await reverseGeocodeToPostcode(result.location.coordinates);
            saveLocation(result.location.coordinates, postcode);
            updatePostcodeDisplay(`<span class="status">${postcode}</span>`, true);
            Logger.success('Postcode displayed', { postcode });
        } catch (postcodeError) {
            Logger.warn('Postcode lookup failed, continuing with coordinates', postcodeError);
            saveLocation(result.location.coordinates);
            updatePostcodeDisplay('<span class="status">Location found</span>', true);
        }

        // Show loading indicator while fetching departures
        showLoadingDepartures();

        // Fetch and display bus departures + train stations (combined, sorted by distance)
        await fetchAndDisplayDepartures(result.location.coordinates);

        // Show help modal for first-time users (after location acquired to avoid blocking permission prompt)
        showHelpIfFirstVisit();
    } catch (error) {
        Logger.error('Failed to initialize:', String(error));
        showPostcodeEntryForm(
            'Location error. Enter postcode:',
            lastSuccessfulPostcode ?? undefined
        );
    }
}

// PWA install event listeners
window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    showInstallBanner();
});

window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    hideInstallBanner();
    localStorage.removeItem(INSTALL_DISMISS_KEY);
    Logger.success('App installed');
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
    if (autoRefreshIntervalId) {
        clearInterval(autoRefreshIntervalId);
    }
});

void init();
